// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (service hours + delivery slot plans: shared client and time helpers for the two screens)
// GET|PUT /admin/service-hours and /admin/delivery-slots (staff-kept PLANS; checkout does not read or enforce them), plus the pure helpers the screens share:
// India Standard Time "now", minutes-of-day maths, and "is ordering open right now according to the saved timetable".
// Neighbours: pages/admin/AdminServiceHours, pages/admin/AdminSlots, utils/adminApi.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";

export interface DayPlan {
  open: boolean;
  intakeStart: string | null; intakeEnd: string | null;
  prepStart: string | null; prepEnd: string | null;
  orderStart: string | null; orderEnd: string | null;
  dispatchCutoff: string | null;
}
export interface HolidayException { date: string; title: string; note: string; closed: boolean; orderStart: string | null; orderEnd: string | null }
export interface Notice { enabled: boolean; message: string }
export interface ServiceHours { configured: boolean; days: DayPlan[]; exceptions: HolidayException[]; notice: Notice; updatedBy: string | null; updatedAt: string | null }
export interface SlotPlan { id: string; name: string; start: string; end: string; capacity: number; fee: number; open: boolean; note: string }
export interface DeliverySlots { configured: boolean; slots: SlotPlan[]; updatedBy: string | null; updatedAt: string | null }

export const fetchServiceHours = () => apiAuthed<ServiceHours>("GET", "/admin/service-hours");
export const saveServiceHours = (plan: Pick<ServiceHours, "days" | "exceptions" | "notice">) => apiAuthed<ServiceHours>("PUT", "/admin/service-hours", plan);
export const fetchDeliverySlots = () => apiAuthed<DeliverySlots>("GET", "/admin/delivery-slots");
export const saveDeliverySlots = (slots: SlotPlan[]) => apiAuthed<DeliverySlots>("PUT", "/admin/delivery-slots", { slots });

export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const PHASES = [
  { key: "intake", label: "Harbor intake", start: "intakeStart", end: "intakeEnd", bar: "bg-ocean-800" },
  { key: "prep", label: "Butchery prep", start: "prepStart", end: "prepEnd", bar: "bg-teal-600" },
  { key: "order", label: "Ordering window", start: "orderStart", end: "orderEnd", bar: "bg-ocean-700" },
] as const;

export const toMin = (hhmm: string | null | undefined) => (hhmm ? Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5)) : null);
/** "05:00" → "5:00 AM" (the screens store 24-hour times, staff read 12-hour ones). */
export function clock(hhmm: string | null | undefined) {
  const m = toMin(hhmm);
  if (m === null) return "—";
  const h = Math.floor(m / 60);
  return `${h % 12 || 12}:${String(m % 60).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}
export const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
export const span = (mins: number) => (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`);

/** The current India Standard Time: weekday index (Monday = 0), minutes since midnight, and the date as YYYY-MM-DD. */
export function istNow(at = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", weekday: "long", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(at).map((p) => [p.type, p.value]));
  const date = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(at);
  return { day: Math.max(0, DAY_NAMES.indexOf(parts.weekday)), minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute), date };
}

/** The ordering window that applies on a date, after holidays: null = closed all day. */
export function windowFor(plan: Pick<ServiceHours, "days" | "exceptions">, dayIndex: number, date: string) {
  const exception = plan.exceptions.find((e) => e.date === date);
  if (exception) return exception.closed ? null : { start: toMin(exception.orderStart), end: toMin(exception.orderEnd), why: exception.title };
  const day = plan.days[dayIndex];
  if (!day?.open) return null;
  const start = toMin(day.orderStart), end = toMin(day.orderEnd);
  return start === null || end === null ? null : { start, end, why: null as string | null };
}

/** Is ordering open right now per the saved timetable, and when does that next change? (A reading of the plan, not of checkout.) */
export function orderingNow(plan: Pick<ServiceHours, "days" | "exceptions">, now = istNow()) {
  const today = windowFor(plan, now.day, now.date);
  if (today && today.start !== null && today.end !== null && now.minutes >= today.start && now.minutes < today.end) return { open: true, changeIn: today.end - now.minutes, why: today.why };
  if (today && today.start !== null && now.minutes < today.start) return { open: false, changeIn: today.start - now.minutes, why: today.why };
  for (let ahead = 1; ahead <= 7; ahead++) {
    const d = new Date(Date.now() + ahead * 864e5);
    const next = istNow(d);
    const w = windowFor(plan, next.day, next.date);
    if (w && w.start !== null) return { open: false, changeIn: 1440 - now.minutes + (ahead - 1) * 1440 + w.start, why: null as string | null };
  }
  return { open: false, changeIn: null, why: today === null ? "Closed today" : null };
}
